import { Plus, X } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { MemberInvite, MemberRole } from "~/api/governanceTypes";
import { useInviteMembers } from "~/api/queries";
import { Select, TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";

/** The server's own ceiling (`UsersController.Invite`): more than 20 is a 400, not a truncation. */
const MAX_INVITES = 20;

interface InviteRow {
  key: string;
  name: string;
  email: string;
  role: MemberRole;
}

let rowSeq = 0;
function emptyRow(role: MemberRole = "member"): InviteRow {
  rowSeq += 1;
  return { key: `invite-${rowSeq}`, name: "", email: "", role };
}

function parseEmails(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,;]/)
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.length > 0 && entry.includes("@")),
    ),
  );
}

/**
 * W5 built the bulk-paste half of this dialog; W10 §7 adds the ROWS the endpoint has always
 * accepted — `users.invite` takes `{ email, name?, role? }` per invite, and a single role for
 * a whole paste could not express "Ada as an admin, the contractors as guests".
 *
 * Clean-room (derived from `UsersController.Actions.cs` only):
 * - the server silently SKIPS an address that is already a member or has no `@`, so
 *   `sent.length` can be smaller than what was submitted — the result reports "invited N of M"
 *   and names what was skipped rather than claiming success;
 * - **20 per call** is a refusal, not a truncation;
 * - role is CLAMPED server-side (a non-Admin inviting an `admin` gets `member`; a Viewer actor
 *   forces `viewer`), so what is offered here is a request, and the result is what landed;
 * - there is no revoke-invite verb: an unaccepted invite is removed with `users.delete`, which
 *   is the Remove entry on the member's own row.
 */
export function InviteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const invite = useInviteMembers();
  const [rows, setRows] = useState<InviteRow[]>([emptyRow()]);
  const [bulk, setBulk] = useState("");
  const [pasting, setPasting] = useState(false);

  const filled = rows.filter((row) => row.email.trim().includes("@"));
  const tooMany = filled.length > MAX_INVITES;

  const update = (key: string, patch: Partial<InviteRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  /** A pasted list becomes rows, so every address can still be given a name and a role. */
  const expandPaste = () => {
    const emails = parseEmails(bulk);
    if (emails.length === 0) {
      return;
    }
    const known = new Set(filled.map((row) => row.email.trim().toLowerCase()));
    const added = emails
      .filter((email) => !known.has(email))
      .map((email) => ({
        ...emptyRow(),
        email,
      }));
    setRows((current) => [...current.filter((row) => row.email.trim() !== ""), ...added]);
    setBulk("");
    setPasting(false);
  };

  const submit = () => {
    if (filled.length === 0 || tooMany) {
      return;
    }
    const invites: MemberInvite[] = filled.map((row) => ({
      email: row.email.trim().toLowerCase(),
      ...(row.name.trim() ? { name: row.name.trim() } : {}),
      role: row.role,
    }));

    invite.mutate(invites, {
      onSuccess: (result) => {
        onClose();
        const sent = result.sent.map((entry) => entry.email.toLowerCase());
        const skipped = invites
          .map((entry) => entry.email)
          .filter((email) => !sent.includes(email));
        if (skipped.length > 0) {
          toast.message(
            t("Invited {{sent}} of {{total}}", { sent: sent.length, total: invites.length }),
            {
              description: t("Already a member or invalid: {{emails}}", {
                emails: skipped.join(", "),
              }),
            },
          );
        } else {
          toast.success(t("Invited {{count}} people", { count: sent.length }));
        }
      },
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Invite people")}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={filled.length === 0 || tooMany || invite.isPending}
            onClick={submit}
          >
            {invite.isPending ? t("Inviting…") : t("Invite")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.key} className="flex items-end gap-2">
              <Input
                label={t("Name")}
                value={row.name}
                fullWidth
                placeholder={t("Optional")}
                onChange={(event) => update(row.key, { name: event.target.value })}
              />
              <Input
                type="email"
                label={t("Email")}
                value={row.email}
                fullWidth
                placeholder="name@example.com"
                onChange={(event) => update(row.key, { email: event.target.value })}
              />
              <Select
                label={t("Role")}
                value={row.role}
                onChange={(event) => update(row.key, { role: event.target.value as MemberRole })}
              >
                <option value="member">{t("Member")}</option>
                <option value="viewer">{t("Viewer")}</option>
                <option value="guest">{t("Guest")}</option>
                <option value="admin">{t("Admin")}</option>
              </Select>
              <IconButton
                icon={X}
                aria-label={t("Remove this row")}
                disabled={rows.length === 1}
                onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
              />
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={Plus}
            disabled={rows.length >= MAX_INVITES}
            // A new row inherits the last row's role: inviting five guests should take five
            // clicks, not ten.
            onClick={() =>
              setRows((current) => [...current, emptyRow(current.at(-1)?.role ?? "member")])
            }
          >
            {t("Add another")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setPasting((open) => !open)}>
            {pasting ? t("Hide the list") : t("Paste a list")}
          </Button>
        </div>

        {pasting ? (
          <div className="flex flex-col gap-2">
            <TextArea
              label={t("Email addresses")}
              hint={t("One per line, or comma-separated. Up to 20 at a time.")}
              rows={4}
              value={bulk}
              onChange={(event) => setBulk(event.target.value)}
            />
            <div>
              <Button variant="neutral" size="sm" onClick={expandPaste}>
                {t("Add these")}
              </Button>
            </div>
          </div>
        ) : null}

        {tooMany ? (
          <p className="text-small text-danger">
            {t("You can only invite up to 20 users at a time.")}
          </p>
        ) : null}

        <Text variant="small">
          {t(
            "Roles are confirmed by the server — if you are not an admin, an admin invite lands as a member.",
          )}
        </Text>
      </div>
    </Dialog>
  );
}
